import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, Shield, BarChart3, Settings, X, Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  acceptAllCookies,
  rejectNonEssentialCookies,
  setConsentPreferences,
  hasConsentBeenGiven
} from '../../utils/cookieConsent'
import { CONSENT_CHANGED_EVENT } from '../../hooks/useAnalytics'
import { trackCookieConsentAccepted, trackCookieConsentRejected, trackCookieConsentCustomized } from '../../config/analyticsEvents'

const CookieConsentBanner = ({ onConsentGiven }) => {
  const [showBanner, setShowBanner] = useState(false)
  const [showCustomize, setShowCustomize] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)

  useEffect(() => {
    // Only show banner if consent hasn't been given yet
    const consentGiven = hasConsentBeenGiven()
    setShowBanner(!consentGiven)
  }, [])

  const dispatchConsentChange = () => {
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
  }

  const handleAcceptAll = () => {
    acceptAllCookies()
    setShowBanner(false)
    dispatchConsentChange()
    trackCookieConsentAccepted()
    onConsentGiven?.()
  }

  const handleRejectNonEssential = () => {
    rejectNonEssentialCookies()
    setShowBanner(false)
    dispatchConsentChange()
    trackCookieConsentRejected()
    onConsentGiven?.()
  }

  const handleSavePreferences = () => {
    setConsentPreferences({
      analytics: analyticsEnabled
    })
    setShowBanner(false)
    dispatchConsentChange()
    trackCookieConsentCustomized(analyticsEnabled)
    onConsentGiven?.()
  }

  if (!showBanner) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-4 right-4 z-[60] max-w-sm"
      >
        <div className="glass-effect bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-gray-200/50 overflow-hidden">
          <AnimatePresence mode="wait">
            {!showCustomize ? (
              <motion.div
                key="main"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-4"
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                    <Cookie className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      We value your privacy
                    </h3>
                    <p className="text-gray-600 text-xs leading-relaxed">
                      We use cookies to enhance your experience and analyze traffic.
                    </p>
                  </div>
                </div>

                {/* Links */}
                <div className="flex gap-3 mb-3 text-xs">
                  <Link 
                    to="/privacy-policy" 
                    className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                  >
                    Privacy
                  </Link>
                  <Link 
                    to="/cookie-policy" 
                    className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                  >
                    Cookies
                  </Link>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2 px-3 rounded-lg text-xs font-semibold transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectNonEssential}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCustomize(true)}
                    className="p-2 border border-gray-200 hover:border-gray-300 text-gray-600 rounded-lg transition-all duration-200 cursor-pointer"
                    aria-label="Customize cookie preferences"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="customize"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-4"
              >
                {/* Customize Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md">
                      <Settings className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Cookie Preferences
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                {/* Cookie Categories */}
                <div className="space-y-3 mb-4">
                  {/* Essential Cookies */}
                  <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded">
                        <Shield className="w-3 h-3 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-gray-900">Essential</h4>
                        <p className="text-[10px] text-gray-500">Required</p>
                      </div>
                    </div>
                    <div className="w-10 h-5 bg-green-500 rounded-full flex items-center justify-end px-0.5">
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded">
                        <BarChart3 className="w-3 h-3 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-gray-900">Analytics</h4>
                        <p className="text-[10px] text-gray-500">Optional</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                      className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-200 ${
                        analyticsEnabled ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-lg text-xs font-semibold transition-all duration-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 flex items-center justify-center gap-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2 px-3 rounded-lg text-xs font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <Check className="w-3 h-3" />
                    <span>Save</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CookieConsentBanner
